import { useSwitch } from "@mui/base/useSwitch";
import { useCallback, useMemo, useState } from "react";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { sendMirrorCustomEvent } from "shared/components/tracking/mirror";
import {
  useFirebaseRegistrationToken,
  useNotificationUtilities,
  useSubscriptionsList,
} from "shared/hooks";

import { Variant as LoadingVariant } from "scmp-app/components/common/loading/enums";
import { getFirebaseApp } from "scmp-app/lib/firebase";
import { firebaseTopic } from "scmp-app/lib/hooks";
import type { liveUpdateSwitchLiveArticle$key } from "scmp-app/queries/__generated__/liveUpdateSwitchLiveArticle.graphql";

import {
  Container,
  StyledLoading,
  SwitchInput,
  SwitchRoot,
  SwitchThumb,
  SwitchTrack,
  Text,
} from "./styles";

type Props = {
  className?: string;
  entityId: string;
  reference: liveUpdateSwitchLiveArticle$key;
};

export const LiveArticleLiveUpdateSwitch: FunctionComponent<Props> = ({
  className,
  entityId,
  reference,
}) => {
  const { refreshRegistrationToken, registrationToken } =
    useFirebaseRegistrationToken(getFirebaseApp());
  const { isDenied, isGranted, requestPermission } = useNotificationUtilities();
  const { setSubscriptionList, subscriptionList = [] } = useSubscriptionsList();

  const liveArticle = useFragment(
    graphql`
      fragment liveUpdateSwitchLiveArticle on LiveArticle {
        status
        pushNotifications
      }
    `,
    reference,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const topic = `live-${entityId}`;
  const isEnabled = useMemo(
    () => !isLoading && liveArticle?.status === true && liveArticle?.pushNotifications === true,
    [isLoading, liveArticle?.pushNotifications, liveArticle?.status],
  );

  const handleSwitchChange = useCallback(
    async (isChecked: boolean) => {
      if (typeof window === "undefined") return;
      if (registrationToken?.status === "error") return;
      if (isDenied) return;

      const payload = {
        token: registrationToken?.result ?? "",
        topic,
      };

      if (!isGranted) {
        const result = await requestPermission();
        if (result !== "granted") return;
        try {
          const refreshedToken = await refreshRegistrationToken();
          payload.token = refreshedToken ?? registrationToken?.result ?? "";
        } catch (error) {
          console.error(error);
          return;
        }
      }

      if (isChecked) {
        sendMirrorCustomEvent({
          event_name: "scmp_alerts",
          topic,
        });
      }
      await (isChecked ? firebaseTopic.subscribe(payload) : firebaseTopic.unsubscribe(payload));

      setSubscriptionList(current => {
        const topics = current ?? [];
        return isChecked ? [...topics, topic] : topics.filter(item => item !== topic);
      });
    },
    [
      isDenied,
      isGranted,
      refreshRegistrationToken,
      registrationToken,
      requestPermission,
      setSubscriptionList,
      topic,
    ],
  );

  const { checked, getInputProps } = useSwitch({
    checked: subscriptionList?.includes(topic) ?? false,
    disabled: !isEnabled,
    onChange: async event => {
      setIsLoading(true);
      await handleSwitchChange(event.target.checked);
      setIsLoading(false);
    },
  });

  if (!liveArticle?.pushNotifications) return null;

  return (
    <Container $isDisabled={!isEnabled} $isLoading={isLoading} className={className}>
      <Text>Enable alerts for all live updates</Text>
      <StyledLoading variant={LoadingVariant.Spinner} />
      <SwitchRoot $isChecked={checked}>
        <SwitchTrack />
        <SwitchThumb />
        <SwitchInput {...getInputProps()} />
      </SwitchRoot>
    </Container>
  );
};

LiveArticleLiveUpdateSwitch.displayName = "LiveArticleLiveUpdateSwitch";
